import { createRouter, createWebHistory } from 'vue-router'

import LoginView from '@/views/public/LoginView.vue'
import RegisterView from '@/views/public/RegisterView.vue'
import DashboardView from '@/views/protected/DashboardView.vue'
import DefaultLayout from '@/views/layouts/DefaultLayout.vue'
import ResetPasswordView from '@/views/public/ResetPasswordView.vue'
import ValidateEmailView from '@/views/public/ValidateEmailView.vue'
import RequestResetPasswordView from '@/views/public/RequestResetPasswordView.vue'
import AppSumoRedemptionView from '@/views/public/AppSumoRedemptionView.vue'
import SettingsView from '@/views/protected/SettingsView.vue'
import SubscriptionView from '@/views/protected/SubscriptionView.vue'
import SubscriptionManageView from '@/views/protected/SubscriptionManageView.vue'
import SubscriptionConfirmationView from '@/views/protected/SubscriptionConfirmationView.vue'
import ScheduledMessagesView from '@/views/protected/ScheduledMessagesView.vue'
import UserView from '@/views/protected/UserView.vue'
import AgentsView from '@/views/protected/AgentsView.vue'
import PhoneBookView from '@/views/protected/PhoneBookView.vue'

import { useWebSiteStore } from '@/stores/website.store.js'

const checkAuthentication = async() => {
  const webSiteStore = useWebSiteStore()

  if (webSiteStore.isLogged()) {
    if (!webSiteStore.isTokenExpired()) {
      webSiteStore.startRefreshTokenTimerAsync()
      return true
    }

    await webSiteStore.refreshTokenAsync()

    if (webSiteStore.isLogged() && !webSiteStore.isTokenExpired()) {
      return true
    }

    return false
  }

  return false
}

export const menuRoutesEnum = Object.freeze({
  HOME: '/',
  LOGIN: '/login',
  LOGOUT: '/logout',
  REGISTER: '/register',
  RESET_PASSWORD: '/reset-password',
  VALIDATE_EMAIL: '/validate-email',
  REQUEST_RESET_PASSWORD: '/request-reset-password',
  SUBSCRIPTIONS: '/subscriptions',
  SUB_MANAGE: '/subscriptions/manage',
  SUB_CONFIRMATION: '/subscriptions/confirmation',
  SCHEDULED: '/scheduled',
  SETTINGS: '/settings',
  USER: '/user',
  AGENTS: '/agents',
  PHONEBOOK: '/phonebook',
  // AppSumo redemption: long, readable URL. Not linked from the home/nav, noindex, excluded
  // from the sitemap -- but reachable by anyone who has the link (obscurity is not security:
  // single-use codes + rate limiting are what protect it).
  APPSUMO_REDEEM: '/appsumo-lifetime-code-redemption',
})

const routes = [
  {
    path: menuRoutesEnum.LOGIN,
    name: 'login',
    component: LoginView,
  },
  {
    path: menuRoutesEnum.LOGOUT,
    name: 'logout',
    beforeEnter: async() => {
      const webSiteStore = useWebSiteStore()
      await webSiteStore.logoutAsync()
      return { name: 'login' }
    },
  },
  {
    path: menuRoutesEnum.RESET_PASSWORD,
    name: 'reset-password',
    component: ResetPasswordView,
  },
  {
    path: menuRoutesEnum.REQUEST_RESET_PASSWORD,
    name: 'request-reset-password',
    component: RequestResetPasswordView,
  },
  {
    path: menuRoutesEnum.VALIDATE_EMAIL,
    name: 'validate-email',
    component: ValidateEmailView,
  },

  {
    path: menuRoutesEnum.REGISTER,
    name: 'register',
    component: RegisterView,
  },
  {
    path: menuRoutesEnum.APPSUMO_REDEEM,
    name: 'appsumo-redemption',
    component: AppSumoRedemptionView,
    // Works in BOTH states: anonymous (creates the account + redeems) and logged
    // (adds a code to the current account) -- so it must not be bounced to home.
    meta: { allowAnonymous: true, allowAuthenticated: true, noindex: true },
  },
  {
    path: menuRoutesEnum.HOME,
    component: DefaultLayout,
    children: [
      {
        path: menuRoutesEnum.HOME,
        name: 'home',
        component: DashboardView,
      },
      {
        path: menuRoutesEnum.SUBSCRIPTIONS,
        name: 'subscriptions',
        component: SubscriptionView,
      },
      {
        path: menuRoutesEnum.SUB_MANAGE,
        name: 'subscriptions-manage',
        component: SubscriptionManageView,
      },
      {
        path: menuRoutesEnum.SUB_CONFIRMATION,
        name: 'subscriptions-confirmation',
        component: SubscriptionConfirmationView,
      },
      {
        path: menuRoutesEnum.SCHEDULED,
        name: 'scheduled-messages',
        component: ScheduledMessagesView,
      },
      {
        path: menuRoutesEnum.SETTINGS,
        name: 'settings',
        component: SettingsView,
      },
      {
        path: menuRoutesEnum.USER,
        name: 'user',
        component: UserView,
      },
      {
        path: menuRoutesEnum.AGENTS,
        name: 'agents',
        component: AgentsView,
      },
      {
        path: menuRoutesEnum.PHONEBOOK,
        name: 'phonebook',
        component: PhoneBookView,
      },

    ],
  },
]

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes,
  scrollBehavior(to, from, savedPosition) {
    return { top: 0, left: 0 }
  }
})
router.beforeEach(async(to, from, next) => {
  const webSiteStore = useWebSiteStore()
  const isAuthenticated = await checkAuthentication()

  const publicRoutes = [
    menuRoutesEnum.LOGIN,
    menuRoutesEnum.REGISTER,
    menuRoutesEnum.RESET_PASSWORD,
    menuRoutesEnum.VALIDATE_EMAIL,
    menuRoutesEnum.REQUEST_RESET_PASSWORD,
    menuRoutesEnum.APPSUMO_REDEEM
  ]

  // 1. Redirect not logged user that tries to access protected routes
  if (!isAuthenticated && !publicRoutes.includes(to.path)) {
    return next({ path: menuRoutesEnum.LOGIN })
  }

  // 2. Redirect logged user that tries to access public routes
  //    (except routes that are meant to work in both states, e.g. AppSumo redemption:
  //     a logged buyer must be able to add further codes to the same account)
  if (isAuthenticated && publicRoutes.includes(to.path) && !to.meta?.allowAuthenticated) {
    return next({ path: menuRoutesEnum.HOME })
  }

  // 3. Warmup service check for Agents route
  // if (to.path === menuRoutesEnum.AGENTS && !webSiteStore.hasWarmupService) {
  //   return next({ path: menuRoutesEnum.HOME })
  // }
  if (to.path === menuRoutesEnum.AGENTS && !webSiteStore.hasActiveSubscription) {
    return next({ path: menuRoutesEnum.HOME })
  }

  // 4. Scheduling service check for Scheduled Messages route
  if (to.path === menuRoutesEnum.SCHEDULED && !webSiteStore.hasSchedulingService) {
    return next({ path: menuRoutesEnum.HOME })
  }

  next()
})

export default router
